/**
 * Money is integer nano-dollars (1e-9 USD) everywhere: a request costs a few thousand tokens at cents per million,
 * so cents or micro-dollars would round real usage to zero. JS numbers hold integers exactly up to ~$9M in nano-USD,
 * which is far beyond any single balance; the database stores bigint.
 */
export const NANO_PER_USD = 1_000_000_000

export function usdToNano(usd: number): number {
  return Math.round(usd * NANO_PER_USD)
}

export function nanoToUsd(nano: number): number {
  return nano / NANO_PER_USD
}

/** $0.04 per 1M tokens -> 40 nano-USD per token. */
export function perMillionToNanoPerToken(usdPerMillion: number): number {
  return Math.round((usdPerMillion * NANO_PER_USD) / 1_000_000)
}

export function nanoPerTokenToPerMillion(nanoPerToken: number): number {
  return (nanoPerToken * 1_000_000) / NANO_PER_USD
}

/** Display a balance: 2 decimals at or above $1, up to 6 significant decimals below (usage costs are tiny). */
export function formatUsd(nano: number): string {
  const usd = nanoToUsd(nano)
  const abs = Math.abs(usd)
  if (abs === 0) return '$0.00'
  if (abs >= 1) return `${usd < 0 ? '-' : ''}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  if (abs >= 0.01) return `${usd < 0 ? '-' : ''}$${abs.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00')}`
  // two significant digits, never exponent notation: $0.00004, $0.0000012
  const decimals = Math.min(9, Math.max(2, 1 - Math.floor(Math.log10(abs))))
  return `${usd < 0 ? '-' : ''}$${abs.toFixed(decimals).replace(/0+$/, '')}`
}
